import { HttpClient } from '@angular/common/http';
import { Injectable, InjectionToken } from '@angular/core';
import {
  IGalleryService,
  IPayload,
  IUpdatePostCodeDevolutionPayload,
  IProductDescription,
} from '@app/shared/interfaces';
import { HttpService, ResponseType } from '@app/shared/services/http/http.service';
import { UrlService } from '@app/shared/services/url/url.service';
import { Observable, of, map, catchError, throwError } from 'rxjs';
import { StatusTradesEnum } from '../models/enum/status-trades.enum';

@Injectable({
  providedIn: 'root',
})
export class IpsRequestsService implements IGalleryService {
  constructor(
    private _httpService: HttpService,
    private _urlService: UrlService,
    private _http: HttpClient
  ) {}

  exportTrades(
    idStatusTrade: number | number[],
    idTrade: number,
    slaTotal?: number[],
    screeningStep?: any[],
    date?: Date
  ): Observable<any> {
    return of({});
  }

  getTrades(
    page: number,
    pageSize: number,
    idStatusTrade: number | number[],
    idTrade: number,
    slaTotal?: number[],
    screeningStep?: any[],
    date?: Date
  ): Observable<any[]> {
    if (page === 0) {
      page = 1;
    }

    const payload = { page, pageSize, idStatusTrade, Filter: idTrade };

    if (!idStatusTrade) {
      delete payload.idStatusTrade;
    }

    if (!idTrade) {
      delete payload.Filter;
    }

    const url = this._urlService.getUrl('COLLECT.tradesFiltred', { payload });
    return this._httpService
      .get(url, payload)
      .pipe(
        map((response) => ({
          ...response,
          tradeOrders: response.tradeOrders.map((res: any) => ({
            ...res,
            showSkipToReturnStatus: res.statusTrade === StatusTradesEnum.CancellationRequested,
          })),
        }))
      )
      .pipe(catchError((error: any) => throwError(error)));
  }

  getTradeByIdTrade(idTrade: number | string): Observable<any> {
    const url = this._urlService.getUrl('COLLECT.tradeByIdTrade', { idTrade });
    return this._httpService.get(url, idTrade).pipe(catchError((error: any) => throwError(error)));
  }

  getAdditionalInfoBy(idTrade: string) {
    const url = this._urlService.getUrl('COLLECT.getAddicionalInfo', { idTrade });
    return this._httpService.get(url, null).pipe(catchError((error: any) => throwError(error)));
  }

  getFilePdf(idTrade: string, idAssignmentTerm: string): Observable<any> {
    const payload = { idTrade, idAssignmentTerm };
    const url = this._urlService.getUrl('COLLECT.downloadPDF', payload);

    let headers: any = new Headers({ 'Content-Type': 'application/pdf' });
    headers = headers.set('Accept', 'application/pdf');

    return this._http
      .get(url, { headers, responseType: 'blob' as 'json' })
      .pipe(catchError((error: any) => throwError(error)));
  }

  getPricing(): Observable<any> {
    const url = this._urlService.getUrl('RECERTIFICATION.getPricing');

    return this._httpService.get(url, null).pipe(catchError((error: any) => throwError(error)));
  }

  getPricingDetails(idLogic: string): Observable<any> {
    const url = this._urlService.getUrl('RECERTIFICATION.getPricingDetails', { idLogic });

    return this._httpService.get(url, idLogic).pipe(catchError((error: any) => throwError(error)));
  }

  editPricingDetails(idLogic: string, payload: IPayload): Observable<IPayload> {
    const url = this._urlService.getUrl('RECERTIFICATION.editPricingDetails');

    return this._httpService
      .put(`${url}/${idLogic}`, payload)
      .pipe(catchError((error: any) => throwError(error)));
  }

  newAnalysisPrice(payload: any) {
    const url = this._urlService.getUrl('COLLECT.newAnalysisPrice');

    return this._httpService
      .put(`${url}`, payload)
      .pipe(catchError((error: any) => throwError(error)));
  }

  updatePostCodeDevolution(payload: IUpdatePostCodeDevolutionPayload) {
    const url = this._urlService.getUrl('COLLECT.updatePostCodeDevolution');

    return this._httpService.put(url, payload).pipe(catchError((error: any) => throwError(error)));
  }

  createStatusTrade(tradeId, tradeStatusId: StatusTradesEnum) {
    const url = this._urlService.getUrl('COLLECT.createStatusTrade', {
      idTrade: tradeId,
      idStatusTrade: tradeStatusId,
    });

    return this._httpService.post(url, {}).pipe(catchError((error: any) => throwError(error)));
  }

  skipTrade(IdTradeExternalTrade: string, TrackingCode) {
    const url = this._urlService.getUrl('COLLECT.skipTrade');
    const payload = {
      IdTradeExternalTrade,
      TrackingCode,
    };

    return this._httpService.post(url, payload).pipe(catchError((error: any) => throwError(error)));
  }

  sendImei(imeiBiped: string, IdTradeExternal: string) {
    const url = this._urlService.getUrl('COLLECT.sendImei');
    const payload = {
      IdTradeExternal,
      imeiBiped,
    };

    return this._httpService
      .put(url, payload)
      .pipe(catchError((error: any) => throwError(() => new Error(error))));
  }

  RollbackToScreeningStep(idTrade: number | string): Observable<any> {
    const url = this._urlService.getUrl('COLLECT.rollbackToScreeningStep', { idTrade });
    return this._httpService
      .delete(url, idTrade)
      .pipe(catchError((error: any) => throwError(error)));
  }

  setAsMisplaced(idTrade: string, isProductReturn: boolean) {
    const url = this._urlService.getUrl('COLLECT.setToMisplaced');
    const payload = {
      idTrade,
      isProductReturn,
    };

    return this._httpService.post(url, payload).pipe(catchError((error: any) => throwError(error)));
  }

  getProductList(): Observable<Array<IProductDescription>> {
    const url = this._urlService.getUrl('COLLECT.getProductList');
    return this._httpService.get(url, null).pipe(catchError((error: any) => throwError(error)));
  }

  updateProduct(idTradeExternal: string, productDescription: IProductDescription) {
    const url = this._urlService.getUrl('COLLECT.updateProductDescription');
    const payload = {
      idTradeExternal,
      NewIdProduct: productDescription.idProduct,
    };

    return this._httpService
      .put(url, payload)
      .pipe(catchError((error: any) => throwError(() => new Error(error))));
  }

  saveEvidence(payload: any) {
    const url = this._urlService.getUrl('COLLECT.uploadFileChunk');
    return this._httpService.post(url, payload);
  }

  closeScreening(idTrade: string) {
    const url = this._urlService.getUrl('COLLECT.postCloseScreening');
    return this._httpService.post(url, { idTrade });
  }

  getProductReturnLabel(idTradeExternal: any) {
    const url = this._urlService.getUrl('COLLECT.getProductReturnLabel', { idTradeExternal });
    return this._httpService
      .get(url, null, ResponseType.ARRAYBUFFER)
      .pipe(catchError((error: any) => throwError(error)));
  }

  getGalleryByTradeId(idTrade: string): Observable<any> {
    const urlSource = 'COLLECT.getTradeGallery';
    const url = this._urlService.getUrl(urlSource, { idTrade });
    return this._httpService.get(url, idTrade);
  }

  updateTradeGallery(idTrade: string, filename: string) {
    const payload = { idTrade, filename };
    const url = this._urlService.getUrl('COLLECT.postRegisterUploadedMedia', {});
    return this._httpService.post(url, payload, ResponseType.JSON);
  }
}

export const IPS_SERVICE = new InjectionToken<IGalleryService>('IpsRequestsService');
